import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ChevronRight, Search } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from '@kostbar/i18n'
import {
  useCaptureCountItem,
  useCurrentCountSession,
  useDeleteCountCapture,
  useUnknownScan,
} from '../hooks/use-count-session'
import {
  useCountAssortment,
  useRefreshAssortmentOnModeFailure,
} from '../hooks/use-count-assortment'
import { useCaptureSubmission } from '../hooks/use-capture-submission'
import { useWakeLock } from '../hooks/use-wake-lock'
import { useCountRestaurantStore } from '../store/count-restaurant-store'
import { useCountUiStore } from '../store/count-ui-store'
import { BarcodeScanner } from '../components/barcode-scanner'
import { QuantitySheet } from '../components/quantity-sheet'
import { CountSearchSheet } from '../components/count-search-sheet'
import { CountOpenListSheet } from '../components/count-open-list-sheet'
import { CountLastCaptureBand } from '../components/count-last-capture-band'
import { scanFeedback } from '../lib/scan-feedback'
import { safeAreaBottom } from '../lib/safe-area'
import { newCaptureId, resultingTotal, undoPlan } from '../lib/capture-flow'
import type { LastCapture } from '../components/count-last-capture-band'
import type { QuantitySheetTarget } from '../components/quantity-sheet'
import type {
  CountAssortmentItem,
  CountItemView,
} from '@/api/endpoints/inventory-count-sessions'
import type { NoStockReason } from '@/shared/lib/stock-kind'
import { inventoryCountSessionsApi } from '@/api/endpoints/inventory-count-sessions'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { ApiError } from '@/api/types'

/** The reason of a `409 product_has_no_stock` from resolve or capture, if that is what it was. */
function noStockReasonOf(err: unknown): NoStockReason | null {
  if (
    !(err instanceof ApiError) ||
    err.status !== 409 ||
    err.code !== 'product_has_no_stock'
  ) {
    return null
  }
  const data = (err.data ?? {}) as { reason?: unknown }
  return data.reason === 'dish' ||
    data.reason === 'variant' ||
    data.reason === 'not_tracked'
    ? data.reason
    : 'dish'
}

interface SheetState {
  target: QuantitySheetTarget
  /** What the entered number means when the sheet opens; the counter can still switch. */
  mode: 'add' | 'replace'
}

/**
 * The capture screen: the camera *is* the screen, and search, the outstanding list, the quantity
 * pad and the session menu are sheets over it.
 *
 * That structure is the point. Before it, the scanner sat inside a `TabsContent`, which Radix
 * unmounts, so every trip to manual search tore down `getUserMedia` and re-imported the ~1 MB WASM
 * detector — and the list of what had been counted lived below the fold. Now the scanner is
 * mounted for as long as this page is and only pauses while a sheet is open.
 *
 * The location's whole assortment is fetched once per session, which is what lets a scan resolve
 * without a round trip, search rank locally, and the progress figure be a true ratio of the
 * must-count set instead of a count that could exceed its own total.
 */
export function CountCapturePage() {
  const { t } = useTranslation('inventory-count')
  const navigate = useNavigate()
  const restaurantId = useCountRestaurantStore((s) => s.restaurantId)
  // The session menu is a drawer in the layout above this page; while it is open the camera must
  // not keep decoding behind it.
  const sessionMenuOpen = useCountUiStore((s) => s.sessionMenuOpen)

  // Nothing here is persisted: react-query holds the session read in memory, so a reload seeds
  // the screen from `GET .../current` again rather than from a stale local copy of the counts.
  const { data: current, refetch } = useCurrentCountSession(restaurantId)
  const sessionId = current?.session.id ?? null
  const capture = useCaptureCountItem(restaurantId, sessionId)
  const unknownScan = useUnknownScan(restaurantId, sessionId)
  const deleteCapture = useDeleteCountCapture(restaurantId, sessionId)
  const assortment = useCountAssortment(restaurantId, sessionId)
  const submission = useCaptureSubmission(capture.mutateAsync)
  useRefreshAssortmentOnModeFailure(submission.failure, restaurantId, sessionId)

  // Keeps the screen (and with it the camera) alive while the counter walks the store.
  useWakeLock(sessionId != null)

  const [sheet, setSheet] = useState<SheetState | null>(null)
  const [unknownCode, setUnknownCode] = useState<string | null>(null)
  const [resolving, setResolving] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [listOpen, setListOpen] = useState(false)
  const [lastCapture, setLastCapture] = useState<LastCapture | null>(null)
  const [undoPending, setUndoPending] = useState(false)
  const undoInFlight = useRef(false)
  /** Capture order of this browser session, most recent first — see CountOpenListSheet. */
  const [recentSkus, setRecentSkus] = useState<Array<string>>([])

  const capturedItems = useMemo(
    () => (current?.items ?? []).filter((item) => item.countedQty != null),
    [current],
  )
  const countedQtyBySku = useMemo(() => {
    const map = new Map<string, number>()
    for (const item of capturedItems) {
      if (item.countedQty != null) map.set(item.sku, item.countedQty)
    }
    return map
  }, [capturedItems])

  const openItems = useMemo(
    () =>
      assortment.items.filter(
        (item) => item.mustCount && !countedQtyBySku.has(item.sku),
      ),
    [assortment.items, countedQtyBySku],
  )
  const mustCountTotal = assortment.mustCountSkus.size
  const countedMustCount = capturedItems.filter((item) =>
    assortment.mustCountSkus.has(item.sku),
  ).length
  // Capturing an article outside the must-count list is legitimate (it gets a stock record at
  // confirmation), it just is not part of the ratio — which is why "n von m" used to exceed m.
  const outsideCount = capturedItems.length - countedMustCount
  const allCounted = assortment.loaded && openItems.length === 0

  /**
   * "Teilmengen zählen" and the unit, from the assortment (CHG-2026-09-stock-usage-and-price-twins,
   * D9). Only an article flagged there gets the comma pad; everything else — including a code the
   * assortment does not carry — keeps the whole-number pad.
   */
  const countingModeFor = useCallback(
    (item: CountAssortmentItem | null) =>
      item?.fractionalCounting === true
        ? { fractional: true, unit: item.unit }
        : {},
    [],
  )

  const expectedFor = useCallback(
    (sku: string) => {
      if (!assortment.showExpected) return null
      return assortment.itemBySku(sku)?.expectedQty ?? null
    },
    [assortment],
  )

  const openForAssortmentItem = useCallback(
    (item: CountAssortmentItem, mode: 'add' | 'replace') => {
      const previousQty = countedQtyBySku.get(item.sku) ?? null
      submission.reset()
      setSearchOpen(false)
      setListOpen(false)
      setSheet({
        target: {
          sku: item.sku,
          name: item.name,
          // The assortment carries no image URLs by design; only the scan path's `resolve` has one.
          imageUrl: null,
          previousQty,
          expectedQty: assortment.showExpected
            ? (item.expectedQty ?? null)
            : null,
          ...countingModeFor(item),
        },
        mode: previousQty != null ? mode : 'add',
      })
    },
    [assortment.showExpected, countedQtyBySku, submission, countingModeFor],
  )

  const openForCapturedItem = useCallback(
    (item: CountItemView) => {
      submission.reset()
      setListOpen(false)
      setSheet({
        target: {
          sku: item.sku,
          name: item.name,
          imageUrl: null,
          previousQty: item.countedQty,
          expectedQty: expectedFor(item.sku),
          ...countingModeFor(assortment.itemBySku(item.sku)),
        },
        // Tapping an already-counted row is a correction: a repeat capture accumulates by design,
        // so re-entering 9 onto a wrong 10 would land on 19.
        mode: 'replace',
      })
    },
    [expectedFor, submission, countingModeFor, assortment],
  )

  const resolveAndOpenSheet = useCallback(
    async (code: string) => {
      if (!restaurantId) return
      // The barcode is on the device: an EAN hit opens the pad immediately, connection or not.
      const local = assortment.resolveLocally(code)
      if (local) {
        openForAssortmentItem(local, 'add')
        return
      }
      // Only a product-info QR code or a code this location does not carry still needs the
      // server — which is also what keeps the unknown-code path working.
      setResolving(true)
      try {
        const product = await inventoryCountSessionsApi.resolveCode(
          restaurantId,
          code,
        )
        const previousQty = countedQtyBySku.get(product.sku) ?? null
        submission.reset()
        setSheet({
          target: {
            sku: product.sku,
            name: product.name,
            imageUrl: product.imageUrl,
            previousQty,
            expectedQty: expectedFor(product.sku),
            ...countingModeFor(assortment.itemBySku(product.sku)),
          },
          mode: 'add',
        })
      } catch (err) {
        const noStock = noStockReasonOf(err)
        if (noStock) {
          // A dish's, variant's or not-tracked article's code (D11): nothing to count here — the
          // server says what to count instead.
          scanFeedback.unknown()
          toast.error(t(`capture.noStock.${noStock}`))
        } else if (err instanceof ApiError && err.status === 404) {
          // The counter is looking at the label, not the screen: the buzz is the signal that this
          // code is not in the catalogue.
          scanFeedback.unknown()
          setUnknownCode(code)
        } else {
          toast.error(t('capture.resolveFailed'))
        }
      } finally {
        setResolving(false)
      }
    },
    [
      restaurantId,
      assortment,
      openForAssortmentItem,
      countedQtyBySku,
      expectedFor,
      submission,
      countingModeFor,
      t,
    ],
  )

  async function submitCapture(qty: number, mode: 'add' | 'replace') {
    const active = sheet
    if (!active) return
    const previousQty = active.target.previousQty
    const accepted = await submission.submit({
      sku: active.target.sku,
      qty,
      mode: mode === 'replace' ? 'set' : 'add',
    })
    // A rejected capture keeps the sheet open with the typed quantity and its own message; the
    // resend action inside the sheet repeats it under the same captureId.
    if (!accepted) return
    setLastCapture({
      sku: active.target.sku,
      name: active.target.name,
      total: resultingTotal(previousQty, qty, mode),
      added: mode === 'add' ? qty : null,
      previousTotal: previousQty,
    })
    setRecentSkus((skus) => [
      active.target.sku,
      ...skus.filter((sku) => sku !== active.target.sku),
    ])
    setSheet(null)
    submission.reset()
    void refetch()
  }

  function removeCapture() {
    const active = sheet
    if (!active) return
    deleteCapture.mutate(active.target.sku, {
      onSuccess: () => {
        toast.success(t('capture.removed'))
        setSheet(null)
        submission.reset()
        setLastCapture((last) =>
          last?.sku === active.target.sku ? null : last,
        )
        void refetch()
      },
      onError: () => toast.error(t('capture.removeFailed')),
    })
  }

  /**
   * Takes the last capture back: the previous total under a fresh `captureId`, or a delete when
   * there was no previous total — setting it to 0 would record "counted 0", which review reads as
   * a shortage to confirm rather than as nothing having been counted.
   */
  async function undoLastCapture() {
    const last = lastCapture
    if (!last || undoInFlight.current) return
    undoInFlight.current = true
    setUndoPending(true)
    const plan = undoPlan(last.previousTotal)
    try {
      if (plan.kind === 'delete') {
        await deleteCapture.mutateAsync(last.sku)
      } else {
        await capture.mutateAsync({
          sku: last.sku,
          qty: plan.qty,
          mode: 'set',
          captureId: newCaptureId(),
        })
      }
      setLastCapture(null)
      void refetch()
    } catch {
      toast.error(t('capture.undoFailed'))
    } finally {
      undoInFlight.current = false
      setUndoPending(false)
    }
  }

  function submitUnknownScan(qty: number) {
    if (!unknownCode) return
    unknownScan.mutate(
      { code: unknownCode, qty },
      {
        onSuccess: () => {
          toast.success(t('capture.unknownRecorded'))
          setUnknownCode(null)
        },
        onError: () => toast.error(t('capture.captureFailed')),
      },
    )
  }

  if (!sessionId) {
    return (
      <div className="flex flex-col items-center gap-4 p-4 py-12 text-center">
        <p className="text-sm text-muted-foreground">
          {t('capture.noSession')}
        </p>
        <Button className="h-12" onClick={() => navigate({ to: '/inventur' })}>
          {t('layout.back')}
        </Button>
      </div>
    )
  }

  const anySheetOpen =
    sheet != null ||
    unknownCode != null ||
    searchOpen ||
    listOpen ||
    resolving ||
    sessionMenuOpen

  return (
    <div className="flex h-full flex-col">
      {/*
        The camera stage fills the screen. The preview itself stays square: the detector decodes
        the centred square of the frame (cropped to the guide rectangle), so stretching the preview
        into a portrait box would put the guide somewhere the decoder is not looking.
      */}
      <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-black">
        <div className="w-full max-w-full">
          <BarcodeScanner paused={anySheetOpen} onHit={resolveAndOpenSheet} />
        </div>
        {resolving && (
          <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/60 text-sm text-white">
            <Spinner className="size-4" />
            {t('capture.resolving')}
          </div>
        )}
      </div>

      {lastCapture != null && (
        <CountLastCaptureBand
          capture={lastCapture}
          pending={undoPending}
          onUndo={undoLastCapture}
        />
      )}

      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        className="flex h-14 shrink-0 items-center gap-3 border-t border-border px-4 text-left"
      >
        <Search
          className="h-5 w-5 shrink-0 text-muted-foreground"
          aria-hidden
        />
        <span className="flex-1 text-base text-muted-foreground">
          {t('capture.searchTrigger')}
        </span>
        <span className="shrink-0 rounded-md bg-secondary px-2 py-1 text-xs text-secondary-foreground">
          {t('capture.searchHintBadge')}
        </span>
      </button>

      <div
        className="shrink-0 border-t border-border px-4 pb-3 pt-3"
        style={safeAreaBottom('0.75rem')}
      >
        {allCounted ? (
          <div className="flex flex-col gap-2">
            {/*
              The way out of counting only becomes a primary action once nothing is outstanding;
              until then it lives in the session menu, out of the thumb's way.
            */}
            <Button
              className="h-12"
              onClick={() => navigate({ to: '/inventory/count/review' })}
            >
              {/*
                A location whose must-count list is empty (nothing with stock on hand) has
                nothing to count, which is not the same statement as having counted it all.
              */}
              {mustCountTotal > 0
                ? t('capture.allCountedToReview', { total: mustCountTotal })
                : t('capture.nothingToCount')}
              <ChevronRight className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              className="h-11"
              onClick={() => setListOpen(true)}
            >
              {t('capture.openListShort')}
            </Button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setListOpen(true)}
            className="flex min-h-11 w-full flex-col justify-center gap-2 text-left"
          >
            <span className="flex w-full items-center gap-2">
              <span className="text-sm font-semibold tabular-nums">
                {assortment.loaded
                  ? t('capture.progressRatio', {
                      done: countedMustCount,
                      total: mustCountTotal,
                    })
                  : t('capture.progressUnknown', {
                      count: capturedItems.length,
                    })}
              </span>
              {assortment.loaded && outsideCount > 0 && (
                <span className="text-sm tabular-nums text-muted-foreground">
                  {t('capture.progressOutside', { extra: outsideCount })}
                </span>
              )}
              <span className="flex-1" />
              <span className="flex items-center gap-1 text-sm font-medium text-muted-foreground">
                {t('capture.listAction')}
                <ChevronRight className="h-4 w-4" />
              </span>
            </span>
            {assortment.loaded && mustCountTotal > 0 && (
              <span
                aria-hidden
                className="block h-1.5 w-full overflow-hidden rounded-full bg-muted"
              >
                <span
                  className="block h-full rounded-full bg-primary"
                  style={{
                    width: `${Math.round((countedMustCount / mustCountTotal) * 100)}%`,
                  }}
                />
              </span>
            )}
          </button>
        )}
      </div>

      <CountSearchSheet
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        items={assortment.items}
        countedQtyBySku={countedQtyBySku}
        onPick={(item) => openForAssortmentItem(item, 'add')}
      />

      <CountOpenListSheet
        open={listOpen}
        onClose={() => setListOpen(false)}
        openItems={openItems}
        capturedItems={capturedItems}
        recentSkus={recentSkus}
        onPickOpen={(item) => openForAssortmentItem(item, 'add')}
        onPickCaptured={openForCapturedItem}
      />

      <QuantitySheet
        target={sheet?.target ?? null}
        mode={sheet?.mode ?? 'add'}
        allowModeSwitch
        pending={submission.pending}
        failure={submission.failure}
        onResend={() => void submission.resend()}
        onClose={() => {
          setSheet(null)
          submission.reset()
        }}
        onSubmit={(qty, mode) => void submitCapture(qty, mode)}
        onDelete={sheet?.target.previousQty != null ? removeCapture : undefined}
      />

      <QuantitySheet
        target={
          unknownCode
            ? {
                sku: unknownCode,
                name: t('capture.unknownCodeTitle'),
                imageUrl: null,
                previousQty: null,
              }
            : null
        }
        onClose={() => setUnknownCode(null)}
        onSubmit={submitUnknownScan}
      />
    </div>
  )
}
